import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { RatingExport, RatingConfig } from "../models";
import { DemographicsSchema, IDENTITY_OPTIONS, TENURE_OPTIONS, AGE_OPTIONS, GENDER_OPTIONS, TRANSGENDER_OPTIONS } from "../../shared/config.schema";
import type { Demographics } from "../../shared/config.schema";

/**
 * Modal for reviewing and submitting ratings.
 * @prop open - Controls modal visibility.
 * @prop ratings - The datasets to be submitted.
 * @prop config - App configuration, used to resolve attribute names.
 * @prop description - Explanatory copy shown at the top of the modal.
 * @prop submitting - True while a submission is in flight; disables the Submit button.
 * @fires close - When the modal is dismissed without submitting.
 * @fires submit - When the user confirms submission (detail: { demographics: Demographics }).
 */
@customElement("bab-rating-submission")
export class BabRatingSubmission extends LitElement {
  static styles = css`
    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(2px);
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.3s ease, visibility 0.3s ease;
    }

    .modal-overlay--visible {
      opacity: 1;
      visibility: visible;
    }

    .modal-content {
      display: flex;
      flex-direction: column;
      width: 90%;
      max-width: 600px;
      /* Leave room for the viewport on small screens */
      max-height: min(90vh, 720px);
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
      overflow: hidden;
      transform: scale(0.95) translateY(20px);
      transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .modal-overlay--visible .modal-content {
      transform: scale(1) translateY(0);
    }

    .modal-header {
      flex-shrink: 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: var(--bab-border-width, 1.5px) solid var(--bab-border);
    }

    .modal-header h3 {
      margin: 0;
      font-size: 1.1rem;
      color: var(--bab-text);
    }

    .modal-close {
      background: transparent;
      border: none;
      color: var(--bab-text-secondary);
      font-size: 1.25rem;
      cursor: pointer;
      padding: 4px;
      line-height: 1;
      transition: color 0.2s;
    }

    .modal-close:hover {
      color: var(--bab-text);
    }

    /* Scrollable region — everything between the fixed header and footer */
    .modal-body {
      flex: 1 1 auto;
      overflow-y: auto;
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    /* ── Section chrome ─────────────────────────────────────── */
    .section-title {
      margin: 0 0 10px 0;
      font-size: 0.8rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--bab-text-secondary);
    }

    .section-subtitle {
      margin: -6px 0 10px 0;
      font-size: 0.8rem;
      color: var(--bab-text-secondary);
    }

    /* ── Description ─────────────────────────────────────────── */
    .description {
      font-size: 0.9rem;
      color: var(--bab-text-secondary);
      line-height: 1.6;
      margin: 0;
    }

    /* ── Ratings summary ─────────────────────────────────────── */
    .dataset + .dataset {
      margin-top: 14px;
    }

    .dataset-name {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;
      font-size: 0.9rem;
      color: var(--bab-text);
      margin-bottom: 6px;
    }

    .dataset-swatch {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      flex-shrink: 0;
      border: 1px solid rgba(0, 0, 0, 0.15);
    }

    .ratings-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.85rem;
    }

    .ratings-table td {
      padding: 4px 0;
      vertical-align: top;
    }

    .ratings-table td:first-child {
      color: var(--bab-text-secondary);
      padding-right: 12px;
      width: 60%;
    }

    .ratings-table td:last-child {
      color: var(--bab-text);
      font-weight: 500;
    }

    .rating-unset {
      color: var(--bab-text-secondary);
      font-style: italic;
      font-weight: 400;
    }

    /* ── Demographics ────────────────────────────────────────── */
    .checkbox-group {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .checkbox-chip {
      position: relative;
    }

    .checkbox-chip input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }

    .checkbox-chip label {
      display: inline-flex;
      align-items: center;
      padding: 6px 12px;
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: 20px;
      font-size: 0.85rem;
      cursor: pointer;
      background: var(--bab-input-bg, #fff);
      color: var(--bab-text);
      transition: background 0.15s, border-color 0.15s, color 0.15s;
      user-select: none;
    }

    .checkbox-chip input[type="checkbox"]:checked + label {
      background: var(--bab-accent, #4a90e2);
      border-color: var(--bab-accent, #4a90e2);
      color: #fff;
    }

    .checkbox-chip input[type="checkbox"]:focus-visible + label {
      outline: 2px solid var(--bab-accent, #4a90e2);
      outline-offset: 2px;
    }

    .checkbox-chip label:hover {
      border-color: var(--bab-accent, #4a90e2);
    }

    .form-grid {
      display: grid;
      gap: 14px;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .form-label {
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--bab-text);
    }

    .form-label .optional {
      font-weight: 400;
      color: var(--bab-text-secondary);
      margin-left: 4px;
    }

    .form-input,
    .form-select {
      padding: 9px 12px;
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      background: var(--bab-input-bg, #fff);
      color: var(--bab-input-text, #000);
      font-size: 0.9rem;
      font-family: inherit;
      box-sizing: border-box;
      transition: border-color 0.2s;
    }

    .form-input:focus,
    .form-select:focus {
      outline: none;
      border-color: var(--bab-accent, #4a90e2);
    }

    /* ── Footer ──────────────────────────────────────────────── */
    .modal-footer {
      flex-shrink: 0;
      padding: 16px 20px;
      background: var(--bab-bg, #f0f4f8);
      border-top: var(--bab-border-width, 1.5px) solid var(--bab-border);
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }

    .btn {
      padding: 9px 20px;
      background: var(--bab-accent, #4a90e2);
      color: #fff;
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      font-size: 0.9rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: opacity 0.15s, transform 0.1s;
    }

    .btn:hover:not(:disabled) {
      opacity: 0.88;
    }

    .btn:active:not(:disabled) {
      transform: scale(0.97);
    }

    .btn--secondary {
      background: var(--bab-button-secondary-bg, #fff);
      color: var(--bab-button-secondary-text, #4a90e2);
    }

    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;

  @property({ type: Boolean }) open = false;
  @property({ type: Array }) ratings: RatingExport[] = [];
  @property({ type: Object }) config!: RatingConfig;
  @property({ type: String }) description = "";
  /** True while a submission is in flight; disables the Submit button to prevent duplicate submissions. */
  @property({ type: Boolean }) submitting = false;

  @state() private _communityIdentity: string[] = ["prefer-not-to-say"];
  @state() private _communityTenure = "prefer-not-to-say";
  @state() private _age = "prefer-not-to-say";
  @state() private _gender = "prefer-not-to-say";
  @state() private _genderSelfDescribe = "";
  @state() private _transgender = "prefer-not-to-say";

  private _onKeyDown = (e: KeyboardEvent) => {
    if (!this.open) return;
    if (e.key === "Escape") { this._close(); return; }
    if (e.key === "Tab") {
      const focusable = Array.from(
        this.shadowRoot!.querySelectorAll<HTMLElement>(
          "button:not(:disabled), input:not(:disabled), select:not(:disabled)"
        )
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = this.shadowRoot!.activeElement;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("keydown", this._onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this._onKeyDown);
  }

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has("open") && this.open) {
      this._communityIdentity = ["prefer-not-to-say"];
      this._communityTenure = "prefer-not-to-say";
      this._age = "prefer-not-to-say";
      this._gender = "prefer-not-to-say";
      this._genderSelfDescribe = "";
      this._transgender = "prefer-not-to-say";
    }
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("open") && this.open) {
      setTimeout(() => {
        this.shadowRoot?.querySelector<HTMLElement>(".modal-close")?.focus();
      }, 50);
    }
  }

  private _toggleIdentity(value: string) {
    if (value === "prefer-not-to-say") {
      this._communityIdentity = ["prefer-not-to-say"];
      return;
    }
    const without = this._communityIdentity.filter(v => v !== "prefer-not-to-say" && v !== value);
    const adding = !this._communityIdentity.includes(value);
    const next = adding ? [...without, value] : without;
    this._communityIdentity = next.length ? next : ["prefer-not-to-say"];
  }

  private _close() {
    this.dispatchEvent(new CustomEvent("close"));
  }

  private _submit() {
    if (this.submitting || !this._hasAnyRating) return;
    const demographics: Demographics = DemographicsSchema.parse({
      communityIdentity: this._communityIdentity,
      communityTenure: this._communityTenure,
      age: this._age,
      gender: this._gender,
      genderSelfDescribe: this._genderSelfDescribe.trim() || undefined,
      transgender: this._transgender,
    });
    this.dispatchEvent(new CustomEvent("submit", { detail: { demographics } }));
  }

  private get _hasAnyRating(): boolean {
    return this.ratings.some(dataset => Object.values(dataset.ratings).some(r => r !== null));
  }

  private _attrName(id: string): string {
    return this.config?.attributes.find(a => a.id === id)?.name ?? id;
  }

  private _renderRatingValue(value: number | null, version: string): unknown {
    if (value === null) return html`<span class="rating-unset">Not rated</span>`;
    const vc = this.config?.versions[version];
    const max = vc?.maxRating ?? "?";
    return html`${value} / ${max}`;
  }

  private _renderRatingsSummary() {
    if (!this.ratings.length) {
      return html`<p class="description">No ratings to display.</p>`;
    }
    return this.ratings.map(dataset => {
      const vc = this.config?.versions[dataset.version];
      const attrIds = vc?.attributeIds ?? Object.keys(dataset.ratings);
      return html`
        <div class="dataset">
          <div class="dataset-name">
            ${dataset.color
              ? html`<span class="dataset-swatch" style="background:${dataset.color}"></span>`
              : ""}
            ${dataset.name || "Unnamed"}
          </div>
          <table class="ratings-table">
            ${attrIds.map(id => html`
              <tr>
                <td>${this._attrName(id)}</td>
                <td>${this._renderRatingValue(dataset.ratings[id] ?? null, dataset.version)}</td>
              </tr>
            `)}
          </table>
        </div>
      `;
    });
  }

  render() {
    return html`
      <div
        class="modal-overlay ${this.open ? "modal-overlay--visible" : ""}"
        aria-hidden="${String(!this.open)}"
        @click="${this._close}"
      >
        <div
          class="modal-content"
          role="dialog"
          aria-modal="true"
          aria-labelledby="submission-title"
          @click="${(e: Event) => e.stopPropagation()}"
        >
          <div class="modal-header">
            <h3 id="submission-title">Submit Ratings</h3>
            <button class="modal-close" aria-label="Close" @click="${this._close}">✕</button>
          </div>

          <div class="modal-body">
            ${this.description ? html`
              <section aria-labelledby="section-desc">
                <p class="section-title" id="section-desc">About this submission</p>
                <p class="description">${this.description}</p>
              </section>
            ` : ""}

            <section aria-labelledby="section-ratings">
              <p class="section-title" id="section-ratings">Ratings summary</p>
              ${this._renderRatingsSummary()}
            </section>

            <section aria-labelledby="section-demo">
              <p class="section-title" id="section-demo">About you</p>
              <p class="section-subtitle">These ratings are self-identification for aggregate statistics only.</p>
              <div class="form-grid">
                <div class="form-field">
                  <span class="form-label" id="identity-label">How do you identify in the community?</span>
                  <div class="checkbox-group" role="group" aria-labelledby="identity-label">
                    ${IDENTITY_OPTIONS.map(({ value, label }) => html`
                      <span class="checkbox-chip">
                        <input
                          type="checkbox"
                          id="ident-${value}"
                          .checked="${this._communityIdentity.includes(value)}"
                          @change="${() => this._toggleIdentity(value)}"
                        />
                        <label for="ident-${value}">${label}</label>
                      </span>
                    `)}
                  </div>
                </div>
                <div class="form-field">
                  <label class="form-label" for="field-tenure">Time in the community</label>
                  <select
                    id="field-tenure"
                    class="form-select"
                    @change="${(e: Event) => this._communityTenure = (e.target as HTMLSelectElement).value}"
                  >
                    ${TENURE_OPTIONS.map(({ value, label }) => html`<option value="${value}" ?selected="${value === this._communityTenure}">${label}</option>`)}
                  </select>
                </div>
                <div class="form-field">
                  <label class="form-label" for="field-age">Age</label>
                  <select
                    id="field-age"
                    class="form-select"
                    @change="${(e: Event) => this._age = (e.target as HTMLSelectElement).value}"
                  >
                    ${AGE_OPTIONS.map(({ value, label }) => html`<option value="${value}" ?selected="${value === this._age}">${label}</option>`)}
                  </select>
                </div>
                <div class="form-field">
                  <label class="form-label" for="field-gender">Gender identity</label>
                  <select
                    id="field-gender"
                    class="form-select"
                    @change="${(e: Event) => this._gender = (e.target as HTMLSelectElement).value}"
                  >
                    ${GENDER_OPTIONS.map(({ value, label }) => html`<option value="${value}" ?selected="${value === this._gender}">${label}</option>`)}
                  </select>
                </div>
                ${this._gender === "self-describe" ? html`
                  <div class="form-field">
                    <label class="form-label" for="field-gender-self">Your gender identity</label>
                    <input
                      id="field-gender-self"
                      class="form-input"
                      type="text"
                      placeholder="Describe your gender identity"
                      .value="${this._genderSelfDescribe}"
                      @input="${(e: Event) => this._genderSelfDescribe = (e.target as HTMLInputElement).value}"
                    />
                  </div>
                ` : ""}
                <div class="form-field">
                  <label class="form-label" for="field-trans">Do you identify as transgender?</label>
                  <select
                    id="field-trans"
                    class="form-select"
                    @change="${(e: Event) => this._transgender = (e.target as HTMLSelectElement).value}"
                  >
                    ${TRANSGENDER_OPTIONS.map(({ value, label }) => html`<option value="${value}" ?selected="${value === this._transgender}">${label}</option>`)}
                  </select>
                </div>
              </div>
            </section>
          </div>

          <div class="modal-footer">
            <button class="btn btn--secondary" @click="${this._close}">Cancel</button>
            <button class="btn" ?disabled="${this.submitting || !this._hasAnyRating}" @click="${this._submit}">
              ${this.submitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-rating-submission": BabRatingSubmission;
  }
}
